import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";

export default class extends Controller {
  static targets = ["input", "label", "preview", "copyButton", "copiedIndicator"];
  static values = { createUrl: String };

  connect() {
    this.generated = new Map(); // key -> { promise, url }
    this.update();
  }

  update() {
    this.copyButtonTarget.disabled = !this.inputTarget.value.trim();
    this.previewTarget.textContent = this.generated.get(this.#currentKey())?.url || "";
    this.#resetCopiedState();
  }

  async copy() {
    if (!this.inputTarget.value.trim()) return;

    const key = this.#currentKey();
    const url = await this.#generateUrl(key);
    if (!url || key !== this.#currentKey()) return;

    this.previewTarget.textContent = url;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      return; // the link stays visible for manual copying
    }

    this.copyButtonTarget.classList.add("hidden");
    this.copiedIndicatorTarget.classList.remove("hidden");

    clearTimeout(this.resetTimeout);
    this.resetTimeout = setTimeout(() => this.#resetCopiedState(), 3000);
  }

  disconnect() {
    clearTimeout(this.resetTimeout);
  }

  // Each prompt+label combination is saved once; concurrent and repeat copies share one request.
  #generateUrl(key) {
    if (!this.generated.has(key)) {
      const entry = { url: null };
      entry.promise = this.#createDeepLink(key, entry);
      this.generated.set(key, entry);
    }
    return this.generated.get(key).promise;
  }

  async #createDeepLink(key, entry) {
    let response;
    try {
      response = await post(this.createUrlValue, {
        body: { prompt: this.inputTarget.value.trim(), label: this.labelTarget.value.trim() },
        responseKind: "json",
      });
    } catch {
      response = null;
    }

    if (response?.ok) {
      const data = await response.json;
      entry.url = data.url;
      return data.url;
    }

    this.generated.delete(key); // allow retrying
    const errors = response ? (await response.json.catch(() => null))?.errors : null;
    if (key === this.#currentKey()) {
      this.previewTarget.textContent = errors?.join(", ") || "Could not generate link.";
    }
    return null;
  }

  #currentKey() {
    return JSON.stringify([this.inputTarget.value.trim(), this.labelTarget.value.trim()]);
  }

  #resetCopiedState() {
    if (this.hasCopyButtonTarget) {
      this.copyButtonTarget.classList.remove("hidden");
    }
    if (this.hasCopiedIndicatorTarget) {
      this.copiedIndicatorTarget.classList.add("hidden");
    }
  }
}
